import type { CashOutRuntimeProjection } from "../gateway/cash-out-client";
import { posRuntimeDb } from "./pos-db";

const CASH_OUT_DOMAIN = "POS_CASH_OUT_RUNTIME_BLIND_V1";
const LEGACY_CASH_OUT_DOMAIN = "POS_CASH_OUT_RUNTIME";

export type CashOutRuntimeScope = {
  organization_id: string;
  branch_id: string;
  node_id: string;
  employee_id: string;
};

function scopeKey(scope: CashOutRuntimeScope) {
  return `org:${scope.organization_id}:branch:${scope.branch_id}:node:${scope.node_id}:employee:${scope.employee_id}`;
}

function projectionKey(scope: CashOutRuntimeScope) {
  return `cash-out-runtime-blind-v1:${scopeKey(scope)}`;
}

function projectionMatchesScope(
  projection: CashOutRuntimeProjection,
  scope: CashOutRuntimeScope,
) {
  return (
    projection.organization_id === scope.organization_id
    && projection.branch_id === scope.branch_id
    && projection.node_id === scope.node_id
    && projection.employee_id === scope.employee_id
  );
}

export async function readLocalCashOutRuntime(
  scope: CashOutRuntimeScope,
): Promise<CashOutRuntimeProjection | null> {
  const key = projectionKey(scope);
  const record = await posRuntimeDb.projections.get(key);
  if (!record || record.domain !== CASH_OUT_DOMAIN || record.scopeKey !== scopeKey(scope)) return null;
  if (!record.payload || typeof record.payload !== "object") return null;

  const projection = record.payload as CashOutRuntimeProjection;
  return projectionMatchesScope(projection, scope) ? projection : null;
}

export async function saveLocalCashOutRuntime(
  scope: CashOutRuntimeScope,
  projection: CashOutRuntimeProjection,
) {
  if (!projectionMatchesScope(projection, scope)) {
    throw new Error("POS_CASH_OUT_LOCAL_SCOPE_MISMATCH");
  }

  const now = new Date().toISOString();
  await posRuntimeDb.projections.put({
    key: projectionKey(scope),
    scopeKey: scopeKey(scope),
    domain: CASH_OUT_DOMAIN,
    revision: projection.generated_at || now,
    payload: projection,
    updatedAt: now,
  });
}

export async function purgeCashOutRuntimeOutsideScope(scope: CashOutRuntimeScope) {
  const keepScope = scopeKey(scope);
  const currentRecords = await posRuntimeDb.projections.where("domain").equals(CASH_OUT_DOMAIN).toArray();
  const legacyRecords = await posRuntimeDb.projections.where("domain").equals(LEGACY_CASH_OUT_DOMAIN).toArray();
  const staleKeys = [
    ...currentRecords.filter((record) => record.scopeKey !== keepScope).map((record) => record.key),
    ...legacyRecords.map((record) => record.key),
  ];
  if (staleKeys.length > 0) await posRuntimeDb.projections.bulkDelete([...new Set(staleKeys)]);
}
